/**
 * 观看历史组件 - 优化版
 * 展示用户的观看历史记录，支持分页、按日期分组、跳转到播放页
 */

import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Box,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  Avatar,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  LinearProgress,
  Pagination,
  Paper,
  Button,
  IconButton,
  Tooltip,
  Divider,
  Skeleton,
  Grid,
  Card,
  CardMedia,
  CardContent,
  CardActionArea,
} from '@mui/material';
import {
  PlayCircle as PlayCircleIcon,
  CheckCircle as CheckCircleIcon,
  Refresh as RefreshIcon,
  PlayArrow as PlayArrowIcon,
  Delete as DeleteIcon,
  History as HistoryIcon,
  LocalFireDepartment as FireIcon,
} from '@mui/icons-material';
import { getWatchHistoryDetails, formatWatchDate, formatDuration, getCurrentUserId, getGuestUserId } from '../../services/watchApi';

// 骨架屏加载组件
const HistorySkeleton = () => (
  <Box sx={{ p: 2 }}>
    {[1, 2, 3, 4, 5].map((i) => (
      <Box key={i} sx={{ display: 'flex', gap: 2, mb: 2, p: 2 }}>
        <Skeleton variant="rounded" width={96} height={56} />
        <Box sx={{ flexGrow: 1 }}>
          <Skeleton variant="text" width="60%" height={28} />
          <Skeleton variant="text" width="40%" height={20} sx={{ mb: 1 }} />
          <Skeleton variant="rounded" width="100%" height={8} />
        </Box>
      </Box>
    ))}
  </Box>
);

// 空状态组件
const EmptyState = ({ onGoExplore }) => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      py: 8,
      px: 4,
    }}
  >
    <Avatar
      sx={{
        width: 100,
        height: 100,
        bgcolor: 'grey.100',
        color: 'grey.400',
        mb: 3,
      }}
    >
      <HistoryIcon sx={{ fontSize: 50 }} />
    </Avatar>
    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}>
      暂无观看历史
    </Typography>
    <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3, textAlign: 'center', maxWidth: 400 }}>
      开始探索精彩剧集吧！每一次观看都会记录在这里，方便你随时继续观看。
    </Typography>
    <Button
      variant="contained"
      startIcon={<FireIcon />}
      onClick={onGoExplore}
      sx={{ borderRadius: 2 }}
    >
      去发现好剧
    </Button>
  </Box>
);

// 观看记录卡片组件
const WatchHistoryCard = ({ item, onPlay, onDelete }) => {
  // 获取进度条颜色
  const getProgressColor = (progress) => {
    if (progress >= 80) return 'success';
    if (progress >= 50) return 'primary';
    if (progress >= 20) return 'warning';
    return 'error';
  };

  return (
    <Card sx={{ display: 'flex', mb: 2, borderRadius: 2, '&:hover': { boxShadow: 3 } }}>
      <CardActionArea
        onClick={() => onPlay(item)}
        sx={{ display: 'flex', alignItems: 'stretch' }}
      >
        {/* 海报区域 - 完全占据卡片左侧高度，宽度 102px (原 128 缩短 20%) */}
        <Box
          sx={{
            position: 'relative',
            width: 102,
            flexShrink: 0,
            alignSelf: 'stretch',
            overflow: 'hidden',
            bgcolor: 'grey.200',
          }}
        >
          <CardMedia
            component="img"
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              display: 'block',
            }}
            image={item.drama_poster || '/api/placeholder/160/90'}
            alt={item.drama_name}
          />
          {/* 播放按钮覆盖层 */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(0,0,0,0.3)',
              opacity: 0,
              transition: 'opacity 0.2s',
              '&:hover': { opacity: 1 },
            }}
          >
            <Avatar sx={{ bgcolor: 'primary.main', width: 48, height: 48 }}>
              <PlayArrowIcon sx={{ fontSize: 32 }} />
            </Avatar>
          </Box>
          {/* 底部进度条 */}
          <LinearProgress
            variant="determinate"
            value={item.progress}
            color={getProgressColor(item.progress)}
            sx={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              height: 3,
              bgcolor: 'rgba(255,255,255,0.3)',
            }}
          />
        </Box>

        {/* 信息区域 - 四行结构 */}
        <CardContent sx={{ flexGrow: 1, py: 1.5, px: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              {/* 第 1 行：剧集名称 */}
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 600,
                  mb: 0.75,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {item.drama_name}
              </Typography>

              {/* 第 2 行：当前观看集数 + 观看中/已看完标签 */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
                <Typography variant="body2" color="text.secondary">
                  第 {item.episode_id} 集
                  {item.total_duration ? ` · ${formatDuration(item.total_duration)}` : ''}
                </Typography>
                {item.is_completed ? (
                  <Chip
                    icon={<CheckCircleIcon fontSize="small" />}
                    label="已看完"
                    size="small"
                    color="success"
                    sx={{ height: 22, '& .MuiChip-icon': { ml: 0.5 } }}
                  />
                ) : (
                  <Chip
                    icon={<PlayCircleIcon fontSize="small" />}
                    label="观看中"
                    size="small"
                    color="primary"
                    sx={{ height: 22, '& .MuiChip-icon': { ml: 0.5 } }}
                  />
                )}
              </Box>

              {/* 第 3 行：进度条 */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
                <Box sx={{ flexGrow: 1, maxWidth: 200 }}>
                  <LinearProgress
                    variant="determinate"
                    value={item.progress}
                    color={getProgressColor(item.progress)}
                    sx={{ borderRadius: 1, height: 6 }}
                  />
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                  {item.progress}%
                </Typography>
              </Box>

              {/* 第 4 行：观看时间 */}
              <Typography variant="caption" color="text.secondary">
                {formatWatchDate(item.last_watched_at)}
              </Typography>
            </Box>

            {/* 操作按钮 */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Tooltip title="继续观看">
                <IconButton
                  size="small"
                  color="primary"
                  onClick={(e) => {
                    e.stopPropagation();
                    onPlay(item);
                  }}
                >
                  <PlayArrowIcon />
                </IconButton>
              </Tooltip>
              {/* 暂时隐藏删除功能，需要后端支持删除接口
              <Tooltip title="删除记录">
                <IconButton
                  size="small"
                  color="error"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(item);
                  }}
                >
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
              */}
            </Box>
          </Box>
        </CardContent>
      </CardActionArea>
    </Card>
  );
};

// 按日期分组的记录列表
const GroupedHistoryList = ({ historyData, onPlay, onDelete }) => {
  // 按日期分组
  const groupedByDate = historyData.reduce((groups, item) => {
    let date = new Date(item.last_watched_at).toLocaleDateString('zh-CN');
    // 处理无效时间戳，把1970/1/1改为2025/1/1
    if (date === '1970/1/1') {
      date = '2025/1/1';
    }
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(item);
    return groups;
  }, {});

  const getDateLabel = (dateStr) => {
    const today = new Date().toLocaleDateString('zh-CN');
    const yesterday = new Date(Date.now() - 86400000).toLocaleDateString('zh-CN');

    if (dateStr === today) return '今天';
    if (dateStr === yesterday) return '昨天';
    return dateStr;
  };

  return (
    <Box>
      {Object.entries(groupedByDate).map(([date, items]) => (
        <Box key={date} sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
              {getDateLabel(date)}
            </Typography>
            <Divider sx={{ flexGrow: 1, ml: 2 }} />
          </Box>
          {items.map((item) => (
            <WatchHistoryCard
              key={item.id}
              item={item}
              onPlay={onPlay}
              onDelete={onDelete}
            />
          ))}
        </Box>
      ))}
    </Box>
  );
};

const WatchHistoryTab = ({ user }) => {
  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [historyData, setHistoryData] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  const PAGE_SIZE = 20;

  // 获取用户ID
  const getUserId = () => {
    if (user && (user.user_id || user._id || user.id)) {
      return user.user_id || user._id || user.id;
    }
    const currentUserId = getCurrentUserId();
    if (currentUserId) {
      return currentUserId;
    }
    return getGuestUserId();
  };

  // 加载观看历史
  const loadHistory = async (pageNum = 1) => {
    try {
      setLoading(true);
      setError(null);

      const userId = getUserId();
      const result = await getWatchHistoryDetails(userId, pageNum, PAGE_SIZE);

      if (result.success) {
        // 过滤掉已下架的剧集
        // 下架判断：检查 status 字段（0=下架, 1=正常）或 is_offline 字段
        const filteredData = (result.data.history || []).filter(item => {
          // 保留状态为正常的剧集
          // status === 1 或 is_offline === false 或 undefined(兼容旧数据)
          if (item.status !== undefined && item.status !== 1) {
            return false;
          }
          if (item.is_offline === true) {
            return false;
          }
          // 兼容字段：offline = true 时过滤
          if (item.offline === true) {
            return false;
          }
          return true;
        });
        
        setHistoryData(filteredData);
        setTotal(result.data.total || 0); // 总数保持后端返回，仅过滤当前页显示
        setTotalPages(result.data.total_pages || 1);
        setPage(result.data.page || 1);
      } else {
        setError(result.message || '获取观看历史失败');
      }
    } catch (err) {
      console.error('加载观看历史失败:', err);
      setError('网络错误，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  // 初始化加载
  useEffect(() => {
    loadHistory(1);
  }, [user]);

  // 分页切换
  const handlePageChange = (event, value) => {
    setPage(value);
    loadHistory(value);
  };

  // 刷新
  const handleRefresh = () => {
    loadHistory(page);
  };

  // 跳转到剧集播放页
  const handlePlay = (item) => {
    // 跳转到剧集详情页并自动播放
    history.push(`/drama/${item.drama_id}?episode=${item.episode_id}&autoPlay=1`);
  };

  // 删除记录（待后端接口支持）
  const handleDelete = (item) => {
    console.log('删除记录:', item.id);
    // 这里可以添加删除确认对话框和API调用
  };

  // 去探索好剧
  const handleGoExplore = () => {
    history.push('/');
  };

  if (loading) {
    return <HistorySkeleton />;
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert
          severity="error"
          sx={{ mb: 2, borderRadius: 2 }}
          action={
            <Button
              color="inherit"
              size="small"
              startIcon={<RefreshIcon />}
              onClick={handleRefresh}
            >
              重试
            </Button>
          }
        >
          {error}
        </Alert>
      </Box>
    );
  }

  if (historyData.length === 0) {
    return <EmptyState onGoExplore={handleGoExplore} />;
  }

  return (
    <Box sx={{ p: 2 }}>
      {/* 头部统计信息 */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
          p: 2,
          bgcolor: 'background.paper',
          borderRadius: 2,
          boxShadow: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar sx={{ bgcolor: 'primary.light', color: 'primary.dark' }}>
            <HistoryIcon />
          </Avatar>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              观看历史
            </Typography>
            <Typography variant="body2" color="text.secondary">
              共 {total} 条观看记录
            </Typography>
          </Box>
        </Box>
        <Button
          variant="outlined"
          size="small"
          startIcon={<RefreshIcon />}
          onClick={handleRefresh}
          sx={{ borderRadius: 2 }}
        >
          刷新
        </Button>
      </Box>

      {/* 历史记录列表（按日期分组） */}
      <GroupedHistoryList
        historyData={historyData}
        onPlay={handlePlay}
        onDelete={handleDelete}
      />

      {/* 分页 */}
      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4, mb: 2 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={handlePageChange}
            color="primary"
            showFirstButton
            showLastButton
            size="large"
            sx={{
              '& .MuiPaginationItem-root': {
                borderRadius: 2,
              },
            }}
          />
        </Box>
      )}
    </Box>
  );
};

export default WatchHistoryTab;
